import React from "react";

const ControlRow = ({ label, children }) => (
  <label className="control-row">
    <span>{label}</span>
    <div className="control-input">{children}</div>
  </label>
);

const DisplayControls = ({
  open,
  onClose,
  settings,
  onSettingChange,
  onReset,
  onPrevSection,
  onNextSection,
  onPrevSong,
  onNextSong,
  onToggleProjectorClock,
  isProjectorClockActive,
  isBlackout,
  toggleBlackout,
  toggleFullScreen,
  onReturnToApp,
  showAutoScroll,
  activeLeaders = [],
  selectedLeaderId,
  onSelectLeader,
  isPresenceOnline = true,
  onRequestLeaderPresence,
  recentBackgroundImages = [],
  onAddRecentBackgroundImage,
  hiddenLines = [],
  onUnhideLine,
  onClearHidden,
}) => {
  return (
    <aside className={`display-drawer ${open ? "open" : ""}`}>
      <div className="drawer-header">
        <div>
          <div className="drawer-title">Display Mode</div>
          <div className="drawer-subtitle">Keyboard: Space/←/→/B/F/P/ESC, Shift+↑/↓ to scroll</div>
        </div>
        <button className="drawer-close" onClick={onClose} aria-label="Close settings">
          ×
        </button>
      </div>

      <section className="drawer-section">
        <div className="drawer-label">Navigation</div>
        <div className="drawer-buttons">
          <button onClick={onPrevSong}>⏮ Song</button>
          <button onClick={onNextSong}>Song ⏭</button>
          <button onClick={onPrevSection}>◀ Section</button>
          <button onClick={onNextSection}>▶ Section</button>
        </div>
      </section>

      <section className="drawer-section">
        <div className="drawer-label">Playback</div>
        <div className="drawer-buttons">
          <button onClick={onToggleProjectorClock}>
            {isProjectorClockActive ? "Stop clock" : "Start clock"}
          </button>
        </div>
        {isPresenceOnline && activeLeaders.length > 0 ? (
          <div className="drawer-buttons" style={{ gridTemplateColumns: "1fr", marginTop: "8px" }}>
            {(selectedLeaderId
              ? activeLeaders.filter((leader) => String(leader.leader_user_id) === String(selectedLeaderId))
              : activeLeaders
            ).map((leader) => {
              const leaderId = String(leader.leader_user_id);
              return (
                <label
                  key={leaderId}
                className="d-flex align-items-center justify-content-between gap-2"
                style={{
                  padding: "6px 8px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "6px",
                }}
              >
                <span>{leader.leader_username || "Leader"}</span>
                <input
                  type="checkbox"
                  checked={String(selectedLeaderId || "") === leaderId}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSelectLeader?.(leaderId);
                    } else {
                      onSelectLeader?.(null);
                    }
                  }}
                />
              </label>
              );
            })}
          </div>
        ) : null}
        {isPresenceOnline && activeLeaders.length === 0 ? (
          <div className="drawer-buttons" style={{ gridTemplateColumns: "1fr", marginTop: "8px" }}>
            <button onClick={() => onRequestLeaderPresence?.({ force: true })}>Check for Leader</button>
          </div>
        ) : null}
      </section>

      <section className="drawer-section">
        <div className="drawer-label">Appearance</div>
        <ControlRow label="Background color">
          <input type="color" value={settings.backgroundColor} onChange={(e) => onSettingChange("backgroundColor", e.target.value)} />
        </ControlRow>
        <ControlRow label="Background image URL">
          <input
            type="text"
            placeholder="https://…"
            list="display-bg-history"
            value={settings.backgroundImage}
            onChange={(e) => onSettingChange("backgroundImage", e.target.value)}
            onBlur={(e) => onAddRecentBackgroundImage?.(e.target.value)}
          />
          <datalist id="display-bg-history">
            {recentBackgroundImages.map((url) => (
              <option key={url} value={url} />
            ))}
          </datalist>
        </ControlRow>
        <ControlRow label="Font color">
          <input type="color" value={settings.fontColor} onChange={(e) => onSettingChange("fontColor", e.target.value)} />
        </ControlRow>
        <ControlRow label="Font size">
          <input type="range" min="28" max="88" value={settings.fontSize} onChange={(e) => onSettingChange("fontSize", Number(e.target.value))} />
          <span className="control-value">{settings.fontSize}px</span>
        </ControlRow>
        <ControlRow label="Line spacing">
          <input type="range" min="1" max="2" step="0.05" value={settings.lineHeight} onChange={(e) => onSettingChange("lineHeight", Number(e.target.value))} />
          <span className="control-value">{settings.lineHeight.toFixed(2)}x</span>
        </ControlRow>
      </section>

      {showAutoScroll ? (
        <section className="drawer-section">
          <div className="drawer-label">Autoscroll</div>
          <ControlRow label="Enable autoscroll">
            <input type="checkbox" checked={settings.autoScroll} onChange={(e) => onSettingChange("autoScroll", e.target.checked)} />
          </ControlRow>
          <ControlRow label="Scroll speed">
            <input type="range" min="0" max="5" step="0.1" value={settings.scrollSpeed} onChange={(e) => onSettingChange("scrollSpeed", Number(e.target.value))} />
            <span className="control-value">{settings.scrollSpeed.toFixed(1)} px/f</span>
          </ControlRow>
        </section>
      ) : null}

      <section className="drawer-section">
        <div className="drawer-label">Quick actions</div>
        <div className="drawer-buttons">
          <button onClick={toggleBlackout}>{isBlackout ? "Show lyrics" : "Black screen"}</button>
          <button onClick={toggleFullScreen}>Full screen</button>
          <button onClick={onReset}>Reset settings</button>
          <button onClick={onReturnToApp}>Back to main app</button>
        </div>
      </section>

      {hiddenLines.length ? (
        <section className="drawer-section">
          <div className="drawer-label">Hidden lines (Shift+click to hide)</div>
          <div className="drawer-buttons" style={{ gridTemplateColumns: "1fr" }}>
            {hiddenLines.map((line) => (
              <div key={line} className="hidden-line-chip">
                <span className="hidden-line-text" title={line}>
                  {line}
                </span>
                <button onClick={() => onUnhideLine?.(line)}>Unhide</button>
              </div>
            ))}
            <button onClick={onClearHidden}>Clear hidden lines</button>
          </div>
        </section>
      ) : null}
    </aside>
  );
};

export default DisplayControls;
